import { useCallback, useMemo, useRef, useState } from 'react';

import CancelIcon from '@mui/icons-material/Cancel';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import SaveIcon from '@mui/icons-material/Save';
import Tooltip from '@mui/material/Tooltip';
import {
  DataGrid,
  GridActionsCellItem,
  GridApi,
  GridColDef,
  GridEventListener,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridRowParams,
} from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import { useDialogs } from '@toolpad/core/useDialogs';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { useNavigate } from 'react-router';

import {
  StudentCourseEnrollment,
  useGetStudentCourseEnrollments,
} from 'api/hooks/private/users/students/GetStudentCourseEnrollments';

import TimeProgressCell from 'components/DataGrid/CellRenderers/TimeProgressCell';
import UpdateCourseEnrollmentDialog from 'components/Dialogs/Courses/UpdateCourseEnrollmentDialog/UpdateCourseEnrollmentDialog';

dayjs.extend(duration);
dayjs.extend(localizedFormat);

interface ProgressProps {
  studentId: string;
}

export default function Progress({ studentId }: ProgressProps) {
  const navigate = useNavigate();
  const dialogs = useDialogs();
  const queryClient = useQueryClient();
  const courseEnrollments = useGetStudentCourseEnrollments({
    variables: {
      id: studentId,
    },
  });

  const apiRef = useRef<GridApi>(null);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

  const handleRowClick = useCallback(
    (params: GridRowParams) => {
      // if the row is not in edit mode
      if (rowModesModel[params.id]?.mode !== GridRowModes.Edit) {
        // navidate to the section roster
        navigate(`/admin/sections/${params.row.course_section_id}`);
      }
    },
    [navigate, rowModesModel],
  );

  const handleEditClick = useCallback(
    (id: GridRowId) => () => {
      dialogs
        .open(UpdateCourseEnrollmentDialog, {
          id: id as string,
        })
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['student_course_enrollments'] });
        });
    },
    [dialogs, queryClient],
  );

  const handleSaveClick = useCallback(
    (id: GridRowId) => () => {
      setRowModesModel((prev) => ({
        ...prev,
        [id]: { mode: GridRowModes.View },
      }));
    },
    [],
  );

  // const handleDeleteClick = (id: GridRowId) => () => {
  //     console.log(`Handle Delete Click: ${id}`);
  // };

  const handleCancelClick = useCallback(
    (id: GridRowId) => () => {
      setRowModesModel((prev) => ({
        ...prev,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      }));
    },
    [],
  );

  const handleRowEditStop: GridEventListener<'rowEditStop'> = useCallback(
    (params, event) => {
      dialogs.alert(JSON.stringify(params, null, 2), {
        title: 'Stopped Editing Row',
      });
      if (params.reason === GridRowEditStopReasons.rowFocusOut) {
        event.defaultMuiPrevented = true;
      }
    },
    [dialogs],
  );

  const processRowUpdate = useCallback(
    async (newRow: GridRowModel<StudentCourseEnrollment>) => {
      // const updatedRow = { ...newRow, isNew: false };
      const newCompletionDate = dayjs(newRow.completed_at);
      // setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
      const confirmed = await dialogs.confirm(
        `Are you sure you want to update the course completion date to ${newCompletionDate.format('ll')}`,
      );

      if (confirmed) {
        queryClient.invalidateQueries({ queryKey: ['student_course_enrollments'] });
      }
      return newRow;
    },
    [dialogs, queryClient],
  );

  const getActions = useCallback(
    (params: GridRowParams<StudentCourseEnrollment>) => {
      const isInEditMode = rowModesModel[params.id]?.mode === GridRowModes.Edit;

      if (isInEditMode) {
        return [
          <GridActionsCellItem key={params.id} icon={<SaveIcon />} label="Save" onClick={handleSaveClick(params.id)} />,
          <GridActionsCellItem
            key={params.id}
            icon={<CancelIcon />}
            label="Cancel"
            className="textPrimary"
            onClick={handleCancelClick(params.id)}
            color="inherit"
          />,
        ];
      }

      return [
        <Tooltip title="Update Completion Date" key={params.id}>
          <GridActionsCellItem
            icon={<EventAvailableIcon />}
            label="Update Completion Date"
            className="textPrimary"
            onClick={handleEditClick(params.id)}
            color="inherit"
          />
        </Tooltip>,
      ];
    },
    [handleCancelClick, handleEditClick, handleSaveClick, rowModesModel],
  );

  const columns: GridColDef<StudentCourseEnrollment>[] = useMemo(
    () => [
      {
        field: 'course_section',
        headerName: 'Section Title',
        type: 'string',
        sortable: false,
        minWidth: 300,
        valueGetter: (_value, row) => {
          return row?.course_section?.title ?? '';
        },
      },
      {
        field: 'enrolled_at',
        headerName: 'Enrolled',
        type: 'dateTime',
        sortable: false,
        width: 150,
        valueGetter: (value) => {
          if (value) {
            return new Date(value);
          }
          return null;
        },
        valueFormatter: (value) => {
          if (value) {
            return dayjs(value).format('ll');
          }
          return null;
        },
      },
      {
        field: 'seat_secured_at',
        headerName: 'Seat Secured',
        type: 'dateTime',
        sortable: false,
        width: 150,
        valueGetter: (value) => {
          if (value) {
            return new Date(value);
          }
          return null;
        },
        valueFormatter: (value) => {
          if (value) {
            return dayjs(value).format('ll');
          }
          return 'No';
        },
      },
      {
        field: 'performance_time_completed',
        headerName: 'Performance Progress',
        type: 'string',
        sortable: false,
        minWidth: 250,
        renderCell: (params) => {
          const courseEnrollment = params.row;
          const courseType = courseEnrollment.course.course_type;
          let timeCompleted = courseEnrollment.performance_time_completed;
          const timeRequired = courseEnrollment.course_completion_criteria.performance_time_required;

          // if enrollment is for an online course and it has been marked as completed
          if (courseType === 'fully_online' && courseEnrollment.completed_at) {
            // set time completed to the time required
            timeCompleted = timeRequired;
          }

          return <TimeProgressCell timeCompleted={timeCompleted} timeRequired={timeRequired} />;
        },
      },
      {
        field: 'observation_time_completed',
        headerName: 'Observation Progress',
        type: 'string',
        sortable: false,
        minWidth: 250,
        renderCell: (params) => {
          const courseEnrollment = params.row;
          const timeCompleted = courseEnrollment.observation_time_completed;
          const timeRequired = courseEnrollment.course_completion_criteria.observation_time_required;

          return <TimeProgressCell timeCompleted={timeCompleted} timeRequired={timeRequired} />;
        },
      },
      {
        field: 'completed_at',
        headerName: 'Completed',
        type: 'date',
        sortable: false,
        width: 150,
        valueGetter: (value) => {
          if (value) {
            return new Date(value);
          }
          return null;
        },
        valueFormatter: (value) => {
          if (value) {
            return dayjs(value).format('ll');
          }
          return null;
        },
      },

      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 100,
        cellClassName: 'actions',
        getActions: getActions,
      },
    ],
    [getActions],
  );

  return (
    <>
      <DataGrid<StudentCourseEnrollment>
        apiRef={apiRef}
        loading={courseEnrollments.isLoading}
        columns={columns}
        rows={courseEnrollments.data ?? []}
        rowModesModel={rowModesModel}
        onRowModesModelChange={setRowModesModel}
        disableColumnFilter
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        onRowClick={handleRowClick}
        sx={{
          '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiInputBase-root': {
            width: 'auto!important',
          },
        }}
      />
    </>
  );
}
